import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, ArrowRight, Quote, Ruler, Play, Snowflake, Wind, Package, Layers, Send, Loader2, Phone, Mail, ShieldCheck, CalendarClock } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { SEO } from '../components/SEO';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '../components/ui/carousel';
import { LandingTrustStrip, LandingFeatureGrid } from '../components/sections/LandingSections';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Approximate capacity guidance, final specs live on each product page.
const MODELS = [
  { name: 'D-Freeze Medium', trays: '4 trays', batch: 'Up to ~10 lbs / batch', best: 'Pilot runs & small batches', to: '/products?segment=entrepreneur' },
  { name: 'D-Freeze Large', trays: '5 trays', batch: '~12 to 16 lbs / batch', best: 'Steady production', to: '/products?segment=entrepreneur' },
  { name: 'D-Freeze XL', trays: '6+ trays', batch: '~20+ lbs / batch', best: 'High-volume operations', to: '/products?segment=commercial-scientific' }
];

const PROCESS = [
  { icon: Layers, title: 'Prep & load', desc: 'Prepare and arrange your product on stainless trays, sliced, whole or liquid, then load and seal the chamber.' },
  { icon: Snowflake, title: 'Freeze', desc: 'The unit rapidly freezes the batch to lock in structure, colour and nutrition before drying begins.' },
  { icon: Wind, title: 'Vacuum & dry', desc: 'A deep vacuum sublimates ice directly to vapour at low temperature, gently removing moisture without heat damage.' },
  { icon: Package, title: 'Package & store', desc: 'Seal the finished product in mylar or jars with oxygen absorbers for a shelf life of up to 25 years.' }
];

const IMG = (id) => `https://images.unsplash.com/photo-${id}?crop=entropy&cs=srgb&fm=jpg&q=85&w=900`;
const FOOD_GALLERY = ['1598799170795-45f90ddfb662', '1641291361624-38b69b86b1cf', '1556216555-becc1cbd8b23', '1598799170815-933217492614'];
const SCI_GALLERY = ['1602052577122-f73b9710adba', '1518152006812-edab29b069ac', '1556216555-becc1cbd8b23', '1598799170815-933217492614'];

const buildFaqs = (ind) => {
  const label = (ind.eyebrow || 'your operation').toLowerCase();
  return [
    { q: `Which D-Freeze model is right for ${label}?`, a: `Most ${label} operations start with the ${ind.recommend}. If you're unsure, our capacity calculator sizes a unit to your target output in under a minute, or talk to a specialist below.` },
    { q: 'How long does a freeze-drying cycle take?', a: 'A typical cycle runs 20 to 40 hours depending on the product, moisture content and load size. The process is fully automated, so it runs unattended day and night.' },
    { q: 'What kind of shelf life can we expect?', a: 'Properly freeze-dried and sealed with oxygen absorbers, products can remain shelf-stable for up to 25 years while retaining flavour, colour and nutrition.' },
    { q: 'Do the results stay consistent batch to batch?', a: 'Yes. Repeatable, documentable cycles are a core reason businesses choose freeze drying over air or spray drying, critical for QA, retail consistency and compliance.' },
    { q: 'What support and warranty is included?', a: 'Every D-Freeze unit ships with documentation, a limited warranty and access to our support team. Rebuild service is available through our partner network.' }
  ];
};

const TIME_WINDOWS = ['Morning (8am-12pm)', 'Afternoon (12 to 4pm)', 'Evening (4 to 7pm)'];

const DemoBooking = ({ slug, industryLabel }) => {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({ name: '', email: '', phone: '', preferred_date: '', preferred_time: TIME_WINDOWS[0], message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [booked, setBooked] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.preferred_date) {
      toast.error('Please add your name, email and a preferred date.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/leads/demo`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        industry: slug,
        preferred_date: form.preferred_date,
        preferred_time: form.preferred_time,
        message: form.message,
        source_url: `${window.location.origin}/industries/${slug}`,
        form_source: `demo-${slug}`
      });
      setBooked(true);
      toast.success("Demo request sent! We'll confirm your slot by email.");
      setForm({ name: '', email: '', phone: '', preferred_date: '', preferred_time: TIME_WINDOWS[0], message: '' });
    } catch (err) {
      console.error('Demo booking failed:', err);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#003456] rounded-3xl p-8 lg:p-10 text-white grid lg:grid-cols-2 gap-10 items-center" data-testid="industry-demo">
      <div>
        <span className="inline-flex items-center gap-2 text-[#CE8946] font-semibold text-sm uppercase tracking-[0.2em]"><CalendarClock className="h-4 w-4" /> Book a live demo</span>
        <h2 className="text-3xl font-bold text-white mt-3 mb-4">See a D Freeze unit in action</h2>
        <p className="text-white/80 leading-relaxed">Pick a time that works for you and one of our specialists will walk you through a live demo tailored to {industryLabel.toLowerCase()}, including sizing, cycle times and expected results. No obligation.</p>
        <ul className="mt-6 space-y-2 text-white/80 text-sm">
          <li className="flex items-center gap-2"><Check className="h-4 w-4 text-[#CE8946]" /> ~30 minute personalised walkthrough</li>
          <li className="flex items-center gap-2"><Check className="h-4 w-4 text-[#CE8946]" /> Live Q&amp;A with a product specialist</li>
          <li className="flex items-center gap-2"><Check className="h-4 w-4 text-[#CE8946]" /> Tailored sizing &amp; ROI guidance</li>
        </ul>
      </div>

      {booked ? (
        <div className="bg-white/10 rounded-2xl p-8 text-center" data-testid="industry-demo-success">
          <div className="w-14 h-14 rounded-full bg-[#CE8946] flex items-center justify-center mx-auto mb-4"><Check className="h-7 w-7 text-[#0a0203]" /></div>
          <h3 className="text-xl font-bold">Request received</h3>
          <p className="text-white/80 mt-2 text-sm">We'll email you shortly to confirm your live demo slot.</p>
          <button onClick={() => setBooked(false)} className="mt-5 text-[#CE8946] font-medium text-sm hover:underline" data-testid="industry-demo-again">Book another slot</button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 text-[#0a0203]" data-testid="industry-demo-form">
          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name *" className="w-full p-3 rounded-lg border border-gray-200 focus:border-[#003456] focus:outline-none" data-testid="industry-demo-name" required />
            <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className="w-full p-3 rounded-lg border border-gray-200 focus:border-[#003456] focus:outline-none" data-testid="industry-demo-phone" />
          </div>
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email *" className="w-full p-3 rounded-lg border border-gray-200 focus:border-[#003456] focus:outline-none mb-3" data-testid="industry-demo-email" required />
          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Preferred date *</label>
              <input type="date" min={today} value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} className="w-full p-3 rounded-lg border border-gray-200 focus:border-[#003456] focus:outline-none" data-testid="industry-demo-date" required />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Preferred time</label>
              <select value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} className="w-full p-3 rounded-lg border border-gray-200 focus:border-[#003456] focus:outline-none bg-white" data-testid="industry-demo-time">
                {TIME_WINDOWS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <textarea rows={2} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Anything specific you'd like to see? (optional)" className="w-full p-3 rounded-lg border border-gray-200 focus:border-[#003456] focus:outline-none resize-none mb-4" data-testid="industry-demo-message" />
          <button type="submit" disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 bg-[#003456] text-white font-semibold px-6 py-3 rounded-md hover:bg-[#002B47] transition-colors disabled:opacity-60" data-testid="industry-demo-submit">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />}
            {submitting ? 'Sending...' : 'Request my demo slot'}
          </button>
        </form>
      )}
    </div>
  );
};

const LeadForm = ({ slug, industryTitle }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in your name, email and message.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/leads/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message,
        form_source: `industry-${slug}`
      });
      toast.success("Thanks! A specialist will be in touch shortly.");
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      console.error('Industry lead submit failed:', err);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-12">
      <div>
        <span className="block text-[#CE8946] font-semibold text-sm uppercase tracking-[0.2em]">Talk to a specialist</span>
        <h2 className="text-3xl font-bold text-[#0a0203] mt-2 mb-4">Request a quote or ask a question</h2>
        <p className="text-gray-600 mb-6">Tell us about your product and target output. Our team will recommend the right D-Freeze setup for your {(industryTitle || 'operation')} and send a tailored quote, no pressure, no obligation.</p>
        <div className="space-y-3 mb-8">
          <a href="tel:1-888-223-1435" className="flex items-center gap-3 text-gray-700 hover:text-[#003456]">
            <span className="w-9 h-9 rounded-full bg-[#F8FAFC] flex items-center justify-center"><Phone className="h-4 w-4 text-[#003456]" /></span>
            1-888-223-1435
          </a>
          <a href="mailto:service@lyphlinefreezedry.com" className="flex items-center gap-3 text-gray-700 hover:text-[#003456]">
            <span className="w-9 h-9 rounded-full bg-[#F8FAFC] flex items-center justify-center"><Mail className="h-4 w-4 text-[#003456]" /></span>
            service@lyphlinefreezedry.com
          </a>
        </div>
        <div className="bg-[#F8FAFC] border border-[#003456]/15 rounded-2xl p-5 flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-[#003456] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-gray-600">Built to North American codes with financing options available. Talk to us before you buy.</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 shadow-sm" data-testid="industry-lead-form">
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full p-3 rounded-lg border border-gray-200 focus:border-[#003456] focus:outline-none" data-testid="industry-lead-name" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full p-3 rounded-lg border border-gray-200 focus:border-[#003456] focus:outline-none" data-testid="industry-lead-phone" />
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full p-3 rounded-lg border border-gray-200 focus:border-[#003456] focus:outline-none" data-testid="industry-lead-email" required />
        </div>
        <div className="mb-5">
          <label className="block text-sm font-medium text-gray-700 mb-1">Tell us about your product & target output *</label>
          <textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full p-3 rounded-lg border border-gray-200 focus:border-[#003456] focus:outline-none resize-none" data-testid="industry-lead-message" required />
        </div>
        <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 bg-[#003456] text-white font-semibold px-6 py-3 rounded-md hover:bg-[#002B47] transition-colors disabled:opacity-60" data-testid="industry-lead-submit">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {submitting ? 'Sending...' : 'Request a Quote'}
        </button>
      </form>
    </div>
  );
};

const IndustryPage = ({ slug }) => {
  const [ind, setInd] = useState(undefined); // undefined=loading, null=not found
  const [related, setRelated] = useState([]);

  useEffect(() => {
    let active = true;
    axios.get(`${API_URL}/api/industries/${slug}`)
      .then(res => { if (active) setInd(res.data); })
      .catch(() => { if (active) setInd(null); });
    return () => { active = false; };
  }, [slug]);

  useEffect(() => {
    if (!ind) return;
    axios.get(`${API_URL}/api/products`)
      .then(res => {
        const all = res.data || [];
        const match = all.filter(p => (p.segment || 'entrepreneur') === ind.segment);
        setRelated((match.length ? match : all).slice(0, 8));
      })
      .catch(() => setRelated([]));
  }, [ind]);

  useEffect(() => {
    if (!ind) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('rebook')) {
      setTimeout(() => {
        document.getElementById('book-demo')?.scrollIntoView({ behavior: 'smooth' });
      }, 400);
    }
  }, [ind]);

  if (ind === null) return <Navigate to="/products" replace />;
  if (ind === undefined) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Navbar />
        <div className="pt-44 pb-40 text-center text-gray-400">Loading…</div>
        <Footer />
      </div>
    );
  }

  const caseStudy = ind.case_study || { quote: '', author: '', stats: [] };
  const faqs = (ind.faqs && ind.faqs.length ? ind.faqs : buildFaqs(ind));
  const galleryRaw = (ind.gallery && ind.gallery.length ? ind.gallery : (ind.segment === 'commercial-scientific' ? SCI_GALLERY : FOOD_GALLERY).map(IMG));
  const gallery = galleryRaw.map(g => typeof g === 'string' ? { url: g, caption: '' } : { url: g.url || '', caption: g.caption || '' }).filter(g => g.url);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid={`industry-${slug}`}>
      <SEO title={ind.title} description={ind.lead} keywords={`${ind.title}, freeze drying, ${ind.eyebrow}, D-Freeze`} />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-44 pb-20 section-padding overflow-hidden">
        <img src={ind.hero_image} alt={ind.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#003456] via-[#003456]/90 to-[#003456]/50"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <span className="block text-[#CE8946] font-semibold text-sm uppercase tracking-[0.2em]">{ind.eyebrow}</span>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mt-3 max-w-3xl">{ind.title}</h1>
          <p className="text-lg text-white/85 mt-4 max-w-xl">{ind.lead}</p>
          <div className="flex flex-wrap gap-4 mt-7">
            <a href="#specialist" className="inline-flex items-center gap-2 bg-[#CE8946] text-[#0a0203] font-semibold px-6 py-3 rounded-md hover:bg-[#00a9b1] transition-colors" data-testid="industry-hero-quote">
              Request a quote <ArrowRight className="h-4 w-4" />
            </a>
            <Link to="/capacity-calculator" className="inline-flex items-center gap-2 border border-white/40 text-white font-semibold px-6 py-3 rounded-md hover:bg-white/10 transition-colors">
              <Ruler className="h-4 w-4" /> Find my size
            </Link>
          </div>
        </div>
      </section>

      <LandingTrustStrip />

      {/* Video */}
      <section className="py-16 section-padding bg-white">
        <div className="max-w-5xl mx-auto text-center">
          <span className="block text-[#CE8946] font-semibold text-sm uppercase tracking-[0.2em]">See it in action</span>          <h2 className="text-3xl font-bold text-[#0a0203] mt-2 mb-8">Freeze drying for {ind.eyebrow.toLowerCase()}</h2>
          <div className="relative rounded-3xl overflow-hidden shadow-lg aspect-video bg-[#003456]" data-testid="industry-video">
            {ind.video_url ? (
              <iframe src={ind.video_url} title={`${ind.title} video`} className="absolute inset-0 w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white bg-gradient-to-br from-[#003456] to-[#002B47]">
                <div className="w-20 h-20 rounded-full bg-[#CE8946] flex items-center justify-center mb-4 shadow-lg">
                  <Play className="h-8 w-8 text-[#0a0203] ml-1" fill="currentColor" />
                </div>
                <p className="font-semibold text-lg">Product walkthrough coming soon</p>
                <p className="text-white/70 text-sm mt-1">In the meantime, request a live demo from our team below.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Why + case study */}
      <section className="py-16 section-padding">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12">
          <div>
            <h2 className="text-3xl font-bold text-[#0a0203] mb-6">Why freeze drying for {ind.eyebrow.toLowerCase()}</h2>
            <div className="space-y-4">
              {(ind.why || []).map((w, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-full bg-[#CE8946] flex items-center justify-center flex-shrink-0 mt-0.5"><Check className="h-4 w-4 text-[#0a0203]" /></span>
                  <span className="text-gray-700">{w}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-[#003456] rounded-3xl p-8 text-white relative overflow-hidden">
            <Quote className="h-10 w-10 text-[#CE8946] mb-4" />
            <p className="text-lg leading-relaxed">"{caseStudy.quote}"</p>
            <p className="text-white/70 text-sm mt-4">- {caseStudy.author}</p>
            <div className="grid grid-cols-3 gap-4 mt-8 pt-6 border-t border-white/15">
              {(caseStudy.stats || []).map((s, i) => (
                <div key={i}>
                  <div className="text-2xl font-bold text-[#CE8946]">{s.v}</div>
                  <div className="text-xs text-white/70 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="mb-10">
            <span className="block text-[#CE8946] font-semibold text-sm uppercase tracking-[0.2em]">How it works</span>
            <h2 className="text-3xl font-bold text-[#0a0203] mt-2">From raw product to shelf stable, in four steps</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="industry-process">
            {PROCESS.map((step, i) => (
              <div key={i} className="bg-[#F8FAFC] rounded-2xl p-6 relative">
                <span className="absolute top-4 right-5 text-4xl font-bold text-[#003456]/10">{i + 1}</span>
                <span className="w-12 h-12 rounded-xl bg-[#003456] flex items-center justify-center mb-4"><step.icon className="h-6 w-6 text-[#CE8946]" /></span>
                <h3 className="font-semibold text-[#0a0203] mb-2">{step.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 section-padding">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <span className="block text-[#CE8946] font-semibold text-sm uppercase tracking-[0.2em]">Results gallery</span>
            <h2 className="text-3xl font-bold text-[#0a0203] mt-2">What operators are producing</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="industry-gallery">
            {gallery.map((item, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden shadow-sm group">
                <img src={item.url} alt={item.caption || `${ind.eyebrow} application ${i + 1}`} className="w-full h-full object-cover aspect-square group-hover:scale-105 transition-transform duration-500" />
                {item.caption && (
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent p-3 pt-8">
                    <p className="text-white text-xs sm:text-sm font-medium leading-snug" data-testid={`industry-gallery-caption-${i}`}>{item.caption}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Spec guidance */}
      <section className="py-16 section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
            <div>
              <h2 className="text-3xl font-bold text-[#0a0203]">Spec &amp; sizing guidance</h2>
              <p className="text-gray-600 mt-2 max-w-2xl">Approximate capacity to help you plan. We recommend the <span className="font-semibold text-[#003456]">{ind.recommend}</span> for most {ind.eyebrow.toLowerCase()} operations.</p>
            </div>
            <Link to="/capacity-calculator" className="inline-flex items-center gap-2 text-[#003456] font-semibold hover:underline">
              Not sure? Use the size calculator <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-gray-100">
            <table className="w-full text-left">
              <thead className="bg-[#F8FAFC] text-[#0a0203]">
                <tr>
                  <th className="p-4 font-semibold">Model</th>
                  <th className="p-4 font-semibold">Trays</th>
                  <th className="p-4 font-semibold">Batch capacity</th>
                  <th className="p-4 font-semibold">Best for</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {MODELS.map((m, i) => (
                  <tr key={i} className={`border-t border-gray-100 ${m.name === ind.recommend ? 'bg-[#CE8946]/5' : ''}`}>
                    <td className="p-4 font-semibold text-[#003456]">
                      {m.name}
                      {m.name === ind.recommend && <span className="ml-2 text-xs bg-[#CE8946] text-[#0a0203] px-2 py-0.5 rounded">Recommended</span>}
                    </td>
                    <td className="p-4 text-gray-700">{m.trays}</td>
                    <td className="p-4 text-gray-700">{m.batch}</td>
                    <td className="p-4 text-gray-700">{m.best}</td>
                    <td className="p-4"><Link to={m.to} className="text-[#003456] font-medium hover:underline whitespace-nowrap">View →</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-400 mt-3">Figures are approximate guidance. See each product page for full specifications.</p>
        </div>
      </section>

      {/* Related products carousel */}
      {related.length > 0 && (
        <section className="py-16 section-padding">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
              <div>
                <span className="block text-[#CE8946] font-semibold text-sm uppercase tracking-[0.2em]">Recommended units</span>
                <h2 className="text-3xl font-bold text-[#0a0203] mt-2">D-Freeze models for {ind.eyebrow.toLowerCase()}</h2>
              </div>
              <Link to={`/products?segment=${ind.segment}`} className="inline-flex items-center gap-2 text-[#003456] font-semibold hover:underline">
                View all models <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <Carousel opts={{ align: 'start' }} className="w-full" data-testid="industry-related">
              <CarouselContent>
                {related.map((p) => (
                  <CarouselItem key={p.id} className="sm:basis-1/2 lg:basis-1/3">
                    <Link to={`/products/${p.id}`} className="block bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all group h-full">
                      <div className="h-48 overflow-hidden bg-[#F8FAFC]">
                        <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                      <div className="p-5">
                        <h3 className="font-semibold text-[#0a0203] group-hover:text-[#003456] transition-colors">{p.name}</h3>
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">{p.description}</p>
                        <span className="text-[#003456] font-medium text-sm flex items-center gap-1 mt-3">View details <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></span>
                      </div>
                    </Link>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className={`hidden ${related.length > 3 ? 'sm:flex' : ''}`} />
              <CarouselNext className={`hidden ${related.length > 3 ? 'sm:flex' : ''}`} />
            </Carousel>
          </div>
        </section>
      )}

      <LandingFeatureGrid title="Built to grow your operation" />

      {/* FAQ */}
      <section className="py-16 section-padding bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8 text-center">
            <span className="block text-[#CE8946] font-semibold text-sm uppercase tracking-[0.2em]">FAQ</span>
            <h2 className="text-3xl font-bold text-[#0a0203] mt-2">Common questions</h2>
          </div>
          <Accordion type="single" collapsible className="w-full" data-testid="industry-faq">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-b border-gray-100">
                <AccordionTrigger className="text-left text-[#0a0203] font-semibold hover:text-[#003456]" data-testid={`industry-faq-q-${i}`}>{f.q}</AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Book a live demo */}
      <section id="book-demo" className="py-16 section-padding scroll-mt-24">
        <div className="max-w-7xl mx-auto">
          <DemoBooking slug={slug} industryLabel={ind.eyebrow} />
        </div>
      </section>

      {/* Lead form */}
      <section id="specialist" className="py-16 section-padding scroll-mt-24">
        <div className="max-w-7xl mx-auto">
          <LeadForm slug={slug} industryTitle={ind.eyebrow} />
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default IndustryPage;
